import { ChangeDetectionStrategy, Component, HostBinding, Input, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { GaugeSection } from './gauge-section'
import { GaugeComponentViewbox } from './gauge-component-viewbox'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { GaugeNeedleComponent } from './gauge-needle/gauge-needle.component'
import { GaugeArcDirective } from './gauge-arc/gauge-arc.directive'
import { GaugeLabelDirective } from './gauge-label/gauge-label.directive'
import { CommonModule } from '@angular/common'

/**
 * @public
 */
export type GaugeElement = NgElement & WithProperties<GaugeComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-gauge',
  templateUrl: './gauge.component.html',
  styleUrls: [ './gauge.component.sass' ],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    GaugeArcDirective,
    GaugeLabelDirective,
    GaugeNeedleComponent
  ]
})
export class GaugeComponent {

  private _min = 0
  private _max = 0
  private _value = 0
  private _angle = 124
  private _outerRadius = 150
  private _innerRadius = this.outerRadius * 0.56
  private _needle = true
  private _needleLength = 140
  private _needleStartWidth = 5
  private _needleEndWidth = 0
  private _labels = true
  private _valueArc = true
  private _sections: GaugeSection[] = []

  @Input()
  @HostBinding()
  title: string

  /**
   * Convert the incoming value to an angle relative the min and max values of the gauge
   *
   * @param value   The incoming value that will be converted
   *
   * @returns       Returns an angle that will be within the starting and ending
   *                angle of the gauge
   */
  relativeAngle(value: number): number {
    return (((value - this.min) / (this.max - this.min)) * this.angle) - this.endAngle
  }

  /**
   * The minimum value of the gauge
   */
  @Input()
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('minValue')
  @HostBinding()
  set min(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._min) {
      this._min = pendingValue
    }
  }

  get min(): number {
    return this._min
  }

  /**
   * The maximum value of the gauge
   */
  @Input()
  @HostBinding()
  set max(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._max) {
      this._max = pendingValue
    }
  }

  get max(): number {
    return this._max
  }

  /**
   * The actual value of the gauge
   */
  @Input()
  @HostBinding()
  set value(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._value) {
      this._value = pendingValue
    }
  }

  get value(): number {
    return this._value
  }

  /**
   * The angle in degrees that the gauge will span
   */
  @Input()
  @HostBinding()
  set angle(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._angle) {
      this._angle = pendingValue
    }
  }

  get angle(): number {
    return this._angle
  }

  /**
   * The outer radius of the gauge
   */
  @Input()
  @HostBinding()
  set outerRadius(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._outerRadius) {
      this._outerRadius = pendingValue
    }
  }

  get outerRadius(): number {
    return this._outerRadius
  }

  /**
   * The inner radius of the gauge
   */
  @Input()
  @HostBinding()
  set innerRadius(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._innerRadius) {
      this._innerRadius = pendingValue
    }
  }

  get innerRadius(): number {
    return this._innerRadius
  }

  /**
   * Determine whether or not to display the needle
   */
  @Input()
  @HostBinding()
  set needle(value: boolean | string) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    if (pendingValue !== this._needle) {
      this._needle = pendingValue
    }
  }

  get needle(): boolean {
    return this._needle
  }

  /**
   * Determine the length of the needle
   */
  @Input()
  @HostBinding()
  set needleLength(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._needleLength) {
      this._needleLength = pendingValue
    }

  }

  get needleLength(): number {
    return this._needleLength
  }

  /**
   * Determine the width of the needle at the base
   */
  @Input()
  @HostBinding()
  set needleStartWidth(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._needleStartWidth) {
      this._needleStartWidth = pendingValue
    }
  }

  get needleStartWidth(): number {
    return this._needleStartWidth
  }

  /**
   * Determine the width of the needle at the point
   */
  @Input()
  @HostBinding()
  set needleEndWidth(value: number | string) {
    let pendingValue = typeof value === 'string' ?
      Number.parseFloat(value) :
      value

    if (pendingValue !== this._needleEndWidth) {
      this._needleEndWidth = pendingValue
    }
  }

  get needleEndWidth(): number {
    return this._needleEndWidth
  }

  /**
   * Toggle the display of the labels
   */
  @Input()
  @HostBinding()
  set labels(value: boolean | string) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    if (pendingValue !== this._labels) {
      this._labels = pendingValue
    }
  }

  get labels(): boolean {
    return this._labels
  }

  /**
   * Toggle the display of the value arc
   */
  @Input()
  @HostBinding()
  set valueArc(value: boolean | string) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    if (pendingValue !== this._valueArc) {
      this._valueArc = pendingValue
    }
  }

  get valueArc(): boolean {
    return this._valueArc
  }

  /**
   * @todo
   *  Find a better way to do the segmented display on the gauge cause the way we pass it now is awful
   */
  @Input()
  @HostBinding()
  set mins(value: string) {
    if (value) {
      let startAngles = value
        ?.split(',')
        ?.map(it => this.relativeAngle(Number.parseFloat(it))) ?? []

      this._sections = startAngles.reduce((collection, startAngle, index) => {
        let section = this._sections?.[index] ?? { startAngle: 0, endAngle: 0 }
        section.startAngle = startAngle
        collection.push(section)
        return collection
      }, [])
    }
  }

  @Input()
  @HostBinding()
  set maxs(value: string) {
    if (value) {
      let endAngles = value
        ?.split(',')
        ?.map(it => this.relativeAngle(Number.parseFloat(it))) ?? []

      this._sections = endAngles.reduce((collection, endAngle, index) => {
        let section = this._sections?.[index] ?? { startAngle: 0, endAngle: 0 }
        section.endAngle = endAngle
        collection.push(section)
        return collection
      }, [])
    }
  }

  @Input()
  @HostBinding()
  set colours(value: string) {
    if (value) {
      let colours = value
        ?.split(',') ?? []

      this._sections = colours.reduce((collection, colour, index) => {
        let section = this._sections?.[index] ?? { startAngle: 0, endAngle: 0 }
        section.style = {
          '--fill-colour': colour
        }
        collection.push(section)
        return collection
      }, [])
    }
  }

  get sections(): GaugeSection[] {
    return this._sections
  }

  /**
   * The starting angle of the gauge
   */
  get startAngle(): number {
    return this.endAngle * -1
  }

  /**
   * The ending angle of the gauge
   */
  get endAngle(): number {
    return this.angle / 2
  }

  /**
   * The value angle of the gauge
   */
  get valueAngle(): number {
    return this.relativeAngle(this.value)
  }

  /**
   * The viewbox of the gauge
   */
  get viewBox(): string {
    return `0 0 ${ GaugeComponentViewbox.WIDTH } ${ GaugeComponentViewbox.HEIGHT }`
  }
}

/**
 * @public
 */
export const registerGaugeElement = registerCustomElementFactory(GaugeComponent)
